import React, { useEffect, useRef, useState } from "react";
import { GripVertical, Eye, EyeOff, Save, Loader2, RotateCcw, ChevronDown, ChevronUp, Image as ImageIcon, Undo2, Trash2, Monitor, Tablet, Smartphone, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DEFAULT_ORDER = ["offers", "explore_menu", "popular", "best_sellers", "why_choose", "rewards", "reviews", "about", "final_cta"];
const SECTION_META = {
  offers: { label: "Today's Offers", fields: ["title", "subtitle"], defs: { title: "Today's Offers", subtitle: "Save more" } },
  explore_menu: { label: "Explore Menu", fields: ["title", "subtitle"], defs: { title: "Explore the Menu", subtitle: "Order Now" } },
  popular: { label: "Popular Products", fields: ["title", "subtitle"], defs: { title: "Popular Products", subtitle: "Loved by locals" } },
  best_sellers: { label: "Best Sellers", fields: ["title", "subtitle"], defs: { title: "Best Sellers", subtitle: "The classics" } },
  why_choose: { label: "Why Choose", fields: ["title", "subtitle"], defs: { title: "Made for you, every time", subtitle: "Why Choose METROS" } },
  rewards: { label: "Rewards", fields: ["title", "subtitle"], defs: { title: "METROS Rewards", subtitle: "Loyalty" } },
  reviews: { label: "Reviews", fields: ["title", "subtitle"], defs: { title: "What locals say", subtitle: "Reviews" } },
  about: { label: "About & Location", fields: ["title", "subtitle"], defs: { title: "More than a takeaway", subtitle: "About" } },
  final_cta: { label: "Final Call-to-Action", fields: ["title", "text"], defs: { title: "Hungry? Let's fix that.", text: "" } },
};

const SPACING_OPTS = [
  { label: "Tight", value: -24 },
  { label: "Default", value: 0 },
  { label: "Comfortable", value: 32 },
  { label: "Spacious", value: 64 },
  { label: "Airy", value: 100 },
];
const PREVIEW_FRAMES = { tablet: { width: 768, height: 1024 }, mobile: { width: 390, height: 780 } };

const clone = (x) => JSON.parse(JSON.stringify(x));

function normalize(data) {
  const hp = data.homepage || {};
  const saved = Array.isArray(hp.sections) ? hp.sections : [];
  const byKey = {};
  saved.forEach((x) => { if (x && x.key) byKey[x.key] = x; });
  const order = [...saved.map((x) => x && x.key).filter((k) => DEFAULT_ORDER.includes(k)), ...DEFAULT_ORDER.filter((k) => !byKey[k])];
  const sections = order.map((k) => ({
    key: k,
    visible: byKey[k] ? byKey[k].visible !== false : true,
    title: byKey[k]?.title || "",
    subtitle: byKey[k]?.subtitle || "",
    text: byKey[k]?.text || "",
    bg_color: byKey[k]?.bg_color || "",
    bg_image: byKey[k]?.bg_image || "",
    text_color: byKey[k]?.text_color || "",
    pad_top: byKey[k]?.pad_top != null ? byKey[k].pad_top : 0,
    pad_bottom: byKey[k]?.pad_bottom != null ? byKey[k].pad_bottom : 0,
    overlay: byKey[k]?.overlay != null ? byKey[k].overlay : 0,
  }));
  return { ...data, homepage: { ...hp, sections } };
}

export default function AdminHomeBuilder() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [offersList, setOffersList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const iframeRef = useRef(null);
  const dragIdx = useRef(null);
  const pickerDrag = useRef(null);
  const sRef = useRef(null);
  const initialRef = useRef(null);
  const [previewMode, setPreviewMode] = useState("desktop");
  sRef.current = s;
  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => { const n = normalize(data); initialRef.current = clone(n); setS(n); }).catch(() => toast.error("Could not load settings"));
    api.get("/offers").then(({ data }) => setOffersList(data || [])).catch(() => {});
    api.get("/admin/products").then(({ data }) => setProductsList(data || [])).catch(() => {});
  }, []);

  const postPreview = () => {
    try { iframeRef.current?.contentWindow?.postMessage({ type: "metros-hp-preview", settings: sRef.current }, "*"); } catch (_) { /* ignore */ }
  };
  useEffect(() => { postPreview(); }, [s]);
  useEffect(() => {
    const onReady = (e) => { if (e && e.data && e.data.type === "metros-hp-preview-ready") postPreview(); };
    window.addEventListener("message", onReady);
    return () => window.removeEventListener("message", onReady);
  }, []);

  if (!isSuper) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
        <h2 className="font-heading font-bold text-xl">Homepage Builder</h2>
        <p className="text-muted-foreground mt-2">Only the super admin can edit the homepage.</p>
      </div>
    );
  }
  if (!s) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-metros-red" size={32} /></div>;

  const sections = s.homepage.sections;
  const hero = s.hero || {};
  const previewFrame = PREVIEW_FRAMES[previewMode] || null;

  const commit = (next) => { setHistory((h) => [...h, s]); setS(next); };
  const setSections = (next) => commit({ ...s, homepage: { ...s.homepage, sections: next } });
  const setHero = (patch) => commit({ ...s, hero: { ...(s.hero || {}), ...patch } });
  const updSec = (i, patch) => setSections(sections.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const move = (from, to) => {
    if (from == null || to < 0 || to >= sections.length || from === to) return;
    const a = [...sections]; const [m] = a.splice(from, 1); a.splice(to, 0, m); setSections(a);
  };
  const toggleId = (field, id) => {
    const cur = s.homepage[field] || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    commit({ ...s, homepage: { ...s.homepage, [field]: next } });
  };
  const reorderId = (field, from, to) => {
    const cur = [...(s.homepage[field] || [])];
    if (from == null || to < 0 || to >= cur.length || from === to) return;
    const [m] = cur.splice(from, 1); cur.splice(to, 0, m);
    commit({ ...s, homepage: { ...s.homepage, [field]: cur } });
  };

  const undo = () => { if (!history.length) return; const prev = history[history.length - 1]; setHistory(history.slice(0, -1)); setS(prev); };
  const discard = () => { if (initialRef.current) { setS(clone(initialRef.current)); setHistory([]); toast.info("Draft discarded"); } };
  const resetLayout = () => commit(normalize({ ...s, homepage: { ...(s.homepage || {}), sections: [] } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { hero: s.hero, homepage: s.homepage, why_choose: s.why_choose, about: s.about });
      initialRef.current = clone(s); setHistory([]);
      toast.success("Homepage published — live now");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const renderPicker = (field, items, labelKey) => {
    const selected = s.homepage[field] || [];
    const byId = {}; items.forEach((it) => { byId[it.id] = it; });
    return (
      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-1">Feature specific items {selected.length ? `(${selected.length} chosen — drag to order)` : "(auto)"}</p>
        {selected.length > 0 && (
          <div className="mb-2 space-y-1">
            {selected.map((id, idx) => (
              <div key={id} draggable data-testid={`hp-chosen-${field}-${id}`}
                onDragStart={() => { pickerDrag.current = idx; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { reorderId(field, pickerDrag.current, idx); pickerDrag.current = null; }}
                className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-sm">
                <span className="cursor-grab active:cursor-grabbing text-gray-300"><GripVertical size={14} /></span>
                <span className="flex-1 truncate">{idx + 1}. {byId[id] ? byId[id][labelKey] : id}</span>
                <button onClick={() => toggleId(field, id)} className="text-gray-400 hover:text-metros-red" title="Remove"><X size={14} /></button>
              </div>
            ))}
          </div>
        )}
        <div className="max-h-40 overflow-y-auto border border-gray-100 rounded-lg p-2 space-y-1">
          {items.map((it) => (
            <label key={it.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid={`hp-pick-${field}-${it.id}`} checked={selected.includes(it.id)} onChange={() => toggleId(field, it.id)} className="accent-metros-red" />
              <span className="truncate">{it[labelKey]}{it.category ? ` · ${it.category}` : ""}</span>
            </label>
          ))}
          {!items.length && <p className="text-xs text-gray-400">None available.</p>}
        </div>
        <p className="text-[11px] text-gray-400 mt-1">Drag chosen items to set their order. Leave all unchecked to auto-pick.</p>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h1 className="font-heading font-extrabold text-2xl">Homepage Builder</h1>
          <p className="text-sm text-muted-foreground">Drag to reorder, toggle sections, set backgrounds and pick featured items — the preview is your live homepage.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="hp-undo" onClick={undo} disabled={!history.length} title="Undo last change" className="inline-flex items-center gap-2 border border-gray-200 rounded-full px-3 py-2 text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-40"><Undo2 size={15} /> Undo</button>
          <button data-testid="hp-discard" onClick={discard} title="Discard all unsaved changes" className="inline-flex items-center gap-2 border border-gray-200 rounded-full px-3 py-2 text-sm font-semibold hover:bg-gray-50 transition-colors"><Trash2 size={15} /> Discard</button>
          <button data-testid="hp-reset" onClick={resetLayout} className="inline-flex items-center gap-2 border border-gray-200 rounded-full px-3 py-2 text-sm font-semibold hover:bg-gray-50 transition-colors"><RotateCcw size={15} /> Reset layout</button>
          <button data-testid="hp-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-metros-red text-white rounded-full px-5 py-2 text-sm font-bold disabled:opacity-60">{busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save &amp; Publish</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(340px,420px)_1fr] gap-5">
        <div className="space-y-4 max-h-[calc(100vh-160px)] overflow-y-auto pr-1">
          <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
            <h3 className="font-heading font-bold flex items-center gap-2"><ImageIcon size={16} className="text-metros-red" /> Hero</h3>
            <label className="block text-xs font-semibold text-muted-foreground">Headline
              <input data-testid="hp-hero-headline" value={hero.headline || ""} onChange={(e) => setHero({ headline: e.target.value })} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm font-normal text-metros-black" /></label>
            <label className="block text-xs font-semibold text-muted-foreground">Subtext
              <textarea data-testid="hp-hero-subtext" value={hero.subtext || ""} onChange={(e) => setHero({ subtext: e.target.value })} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm h-16 resize-none font-normal text-metros-black" /></label>
            <label className="block text-xs font-semibold text-muted-foreground">Primary button label
              <input data-testid="hp-hero-cta" value={hero.primary_cta || ""} onChange={(e) => setHero({ primary_cta: e.target.value })} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm font-normal text-metros-black" /></label>
            <label className="block text-xs font-semibold text-muted-foreground">Hero image URL
              <input data-testid="hp-hero-image" value={hero.image || ""} onChange={(e) => setHero({ image: e.target.value })} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm font-normal text-metros-black" /></label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">Background
                <input data-testid="hp-hero-bg" type="color" value={/^#[0-9a-fA-F]{6}$/.test(hero.bg_color || "") ? hero.bg_color : "#0A0A0A"} onChange={(e) => setHero({ bg_color: e.target.value })} className="h-8 w-12 rounded border border-gray-200 cursor-pointer" /></label>
              <label className="flex-1 text-xs font-semibold text-muted-foreground">Opacity {hero.bg_opacity != null ? hero.bg_opacity : 80}%
                <input data-testid="hp-hero-opacity" type="range" min={0} max={100} value={hero.bg_opacity != null ? hero.bg_opacity : 80} onChange={(e) => setHero({ bg_opacity: Number(e.target.value) })} className="w-full accent-metros-red" /></label>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <h3 className="font-heading font-bold mb-2">Sections</h3>
            <div className="space-y-2">
              {sections.map((sec, i) => {
                const meta = SECTION_META[sec.key] || { label: sec.key, fields: [], defs: {} };
                const open = expanded === sec.key;
                return (
                  <div key={sec.key} data-testid={`hp-section-${sec.key}`} draggable
                    onDragStart={() => { dragIdx.current = i; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { move(dragIdx.current, i); dragIdx.current = null; }}
                    className={`border rounded-xl bg-white ${sec.visible ? "border-gray-200" : "border-dashed border-gray-300 opacity-70"}`}>
                    <div className="flex items-center gap-2 p-2.5">
                      <span className="cursor-grab active:cursor-grabbing text-gray-300"><GripVertical size={16} /></span>
                      <div className="flex flex-col -my-1">
                        <button data-testid={`hp-up-${sec.key}`} onClick={() => move(i, i - 1)} disabled={i === 0} className="text-gray-400 hover:text-metros-black disabled:opacity-30"><ChevronUp size={14} /></button>
                        <button data-testid={`hp-down-${sec.key}`} onClick={() => move(i, i + 1)} disabled={i === sections.length - 1} className="text-gray-400 hover:text-metros-black disabled:opacity-30"><ChevronDown size={14} /></button>
                      </div>
                      <button onClick={() => setExpanded(open ? null : sec.key)} className="flex-1 text-left text-sm font-semibold">{meta.label}</button>
                      <button data-testid={`hp-vis-${sec.key}`} onClick={() => updSec(i, { visible: !sec.visible })} title={sec.visible ? "Visible — click to hide" : "Hidden — click to show"} className="text-gray-500 hover:text-metros-black">{sec.visible ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                    </div>
                    {open && (
                      <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
                        {meta.fields.map((f) => (
                          <label key={f} className="block text-xs font-semibold text-muted-foreground capitalize">{f === "text" ? "Text" : f}
                            {f === "text" ? (
                              <textarea data-testid={`hp-${sec.key}-${f}`} value={sec[f] || ""} placeholder={meta.defs[f] || "Default"} onChange={(e) => updSec(i, { [f]: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm h-14 resize-none font-normal text-metros-black" />
                            ) : (
                              <input data-testid={`hp-${sec.key}-${f}`} value={sec[f] || ""} placeholder={meta.defs[f] || "Default"} onChange={(e) => updSec(i, { [f]: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-normal text-metros-black" />
                            )}
                          </label>
                        ))}

                        <div className="flex items-center gap-3 pt-1">
                          <label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">Background colour
                            <input type="color" data-testid={`hp-${sec.key}-bgcolor`} value={/^#[0-9a-fA-F]{6}$/.test(sec.bg_color || "") ? sec.bg_color : "#ffffff"} onChange={(e) => updSec(i, { bg_color: e.target.value })} className="h-8 w-12 rounded border border-gray-200 cursor-pointer" /></label>
                          {sec.bg_color && <button data-testid={`hp-${sec.key}-bgcolor-clear`} onClick={() => updSec(i, { bg_color: "" })} className="text-xs font-semibold text-gray-500 hover:text-metros-black">Clear</button>}
                        </div>
                        <label className="block text-xs font-semibold text-muted-foreground">Background image URL
                          <input data-testid={`hp-${sec.key}-bgimage`} value={sec.bg_image || ""} placeholder="Optional" onChange={(e) => updSec(i, { bg_image: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-normal text-metros-black" /></label>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">Text colour
                            <input type="color" data-testid={`hp-${sec.key}-textcolor`} value={/^#[0-9a-fA-F]{6}$/.test(sec.text_color || "") ? sec.text_color : "#0A0A0A"} onChange={(e) => updSec(i, { text_color: e.target.value })} className="h-8 w-12 rounded border border-gray-200 cursor-pointer" /></label>
                          {sec.text_color && <button data-testid={`hp-${sec.key}-textcolor-clear`} onClick={() => updSec(i, { text_color: "" })} className="text-xs font-semibold text-gray-500 hover:text-metros-black">Clear</button>}
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <label className="block text-xs font-semibold text-muted-foreground">Top spacing
                            <select data-testid={`hp-${sec.key}-padtop`} value={sec.pad_top ?? 0} onChange={(e) => updSec(i, { pad_top: Number(e.target.value) })} className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm font-normal text-metros-black bg-white">
                              {SPACING_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select></label>
                          <label className="block text-xs font-semibold text-muted-foreground">Bottom spacing
                            <select data-testid={`hp-${sec.key}-padbottom`} value={sec.pad_bottom ?? 0} onChange={(e) => updSec(i, { pad_bottom: Number(e.target.value) })} className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm font-normal text-metros-black bg-white">
                              {SPACING_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select></label>
                        </div>
                        {sec.bg_image ? (
                          <label className="block text-xs font-semibold text-muted-foreground">Overlay dim {sec.overlay || 0}%
                            <input type="range" data-testid={`hp-${sec.key}-overlay`} min={0} max={80} value={sec.overlay || 0} onChange={(e) => updSec(i, { overlay: Number(e.target.value) })} className="w-full accent-metros-red" />
                            <span className="block text-[11px] text-gray-400 font-normal">Darkens the background image so text stays readable.</span></label>
                        ) : null}

                        {sec.key === "offers" && renderPicker("offers_ids", offersList, "title")}
                        {sec.key === "popular" && renderPicker("popular_ids", productsList, "name")}
                        {sec.key === "best_sellers" && renderPicker("best_ids", productsList, "name")}

                        {sec.key === "about" && (
                          <label className="block text-xs font-semibold text-muted-foreground">About paragraph
                            <textarea data-testid="hp-about-body" value={s.about || ""} onChange={(e) => commit({ ...s, about: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm h-20 resize-none font-normal text-metros-black" /></label>
                        )}
                        {sec.key === "why_choose" && (
                          <div className="space-y-2">
                            {(s.why_choose || []).map((w, wi) => (
                              <div key={wi} className="border border-gray-100 rounded-lg p-2 space-y-1">
                                <input value={w.title || ""} onChange={(e) => commit({ ...s, why_choose: s.why_choose.map((x, k) => (k === wi ? { ...x, title: e.target.value } : x)) })} className="w-full rounded border border-gray-200 px-2 py-1 text-sm" placeholder="Title" />
                                <input value={w.desc || ""} onChange={(e) => commit({ ...s, why_choose: s.why_choose.map((x, k) => (k === wi ? { ...x, desc: e.target.value } : x)) })} className="w-full rounded border border-gray-200 px-2 py-1 text-sm" placeholder="Description" />
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="text-[11px] text-gray-400">Leave a field blank to keep the default wording.</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-100">
            <span className="flex items-center gap-2 text-xs font-semibold text-muted-foreground"><Eye size={14} /> Live preview — changes aren't public until you Save &amp; Publish</span>
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-0.5">
              <button data-testid="hp-preview-desktop" onClick={() => setPreviewMode("desktop")} title="Desktop preview" className={`p-1.5 rounded-full transition-colors ${previewMode === "desktop" ? "bg-white shadow text-metros-black" : "text-gray-400"}`}><Monitor size={15} /></button>
              <button data-testid="hp-preview-tablet" onClick={() => setPreviewMode("tablet")} title="Tablet preview" className={`p-1.5 rounded-full transition-colors ${previewMode === "tablet" ? "bg-white shadow text-metros-black" : "text-gray-400"}`}><Tablet size={15} /></button>
              <button data-testid="hp-preview-mobile" onClick={() => setPreviewMode("mobile")} title="Mobile preview" className={`p-1.5 rounded-full transition-colors ${previewMode === "mobile" ? "bg-white shadow text-metros-black" : "text-gray-400"}`}><Smartphone size={15} /></button>
            </div>
          </div>
          <div className={previewFrame ? "flex justify-center bg-gray-100 py-4" : ""} style={{ height: "calc(100vh - 210px)", overflow: "auto" }}>
            <iframe data-testid="hp-preview-frame" ref={iframeRef} title="Homepage preview" src="/" onLoad={postPreview}
              className="bg-white"
              style={previewFrame
                ? { width: previewFrame.width, height: previewFrame.height, border: "1px solid #e5e7eb", borderRadius: 24, boxShadow: "0 10px 40px rgba(0,0,0,0.15)", flex: "0 0 auto" }
                : { width: "100%", height: "100%", border: 0 }} />
          </div>
        </div>
      </div>
    </div>
  );
}
